import {
  createEmptyConfirmations,
  createEmptyOnboardingSession,
  createEmptyPersonContact,
  hasPaidMediaModule,
  PRE_ACTIVATION_OTHER_OPTION_VALUE,
} from "./agencyFormConfig";

const sanitizeOtherText = ({ isOther, value }) => {
  if (!isOther) return "";
  return typeof value === "string" ? value.trim() : "";
};

export const normalizePreActivationWebsiteUrl = (value) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY = "agency";
export const PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND = "brand";

export const SHARED_FIELD_KEYS = [
  "fullName",
  "email",
  "businessName",
  "jobTitle",
  "whatsApp",
  "websiteUrl",
];

export const BRAND_ONLY_FIELD_KEYS = [
  "whatYouSell",
  "averagePrice",
  "idealCustomer",
  "whereCustomerBuys",
  "hasAds",
  "adPlatforms",
  "adPlatformsOther",
  "monthlyAdSpend",
  "currentMetrics",
  "targetCpa",
  "bestCampaignMessages",
  "monthlySales",
  "salesGoal60to90",
  "buzzlyFocus",
  "creativeMaterialLevel",
  "creativeMaterialLocation",
  "additionalMeetingNotes",
  "meetingAccess",
  "accessNotReady",
];

export const AGENCY_ONLY_FIELD_KEYS = [
  "backupContact",
  "leadershipContact",
  "agencyModules",
  "agencyAdPlatforms",
  "teamFamiliarity",
  "sessionBrandName",
  "confirmations",
  "onboardingSession",
];

export const AGENCY_LEGACY_FIELD_KEYS = [
  "platformUsersCount",
  "brandAssetsReady",
  "creativeBottleneck",
  "usesAdAutomation",
  "hasMetaBmAdminAccess",
];

export const sharedDefaultValues = {
  fullName: "",
  email: "",
  businessName: "",
  jobTitle: "",
  whatsApp: "",
  websiteUrl: "",
};

export const brandDefaultValues = {
  whatYouSell: "",
  averagePrice: "",
  idealCustomer: "",
  whereCustomerBuys: [],
  hasAds: "",
  adPlatforms: [],
  adPlatformsOther: "",
  monthlyAdSpend: "",
  currentMetrics: "",
  targetCpa: "",
  bestCampaignMessages: "",
  monthlySales: "",
  salesGoal60to90: "",
  buzzlyFocus: "",
  creativeMaterialLevel: "",
  creativeMaterialLocation: "",
  additionalMeetingNotes: "",
  meetingAccess: [],
  accessNotReady: "",
};

export const agencyDefaultValues = {
  backupContact: createEmptyPersonContact(),
  leadershipContact: createEmptyPersonContact(),
  agencyModules: [],
  agencyAdPlatforms: [],
  teamFamiliarity: "",
  sessionBrandName: "",
  confirmations: createEmptyConfirmations(),
  onboardingSession: createEmptyOnboardingSession(),
};

export const defaultFormValues = {
  organizationType: PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
  ...sharedDefaultValues,
  ...brandDefaultValues,
  ...agencyDefaultValues,
};

export const getOrganizationTypeFromRecord = (raw = {}) => {
  if (raw.organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
    return PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY;
  }
  return PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND;
};

const pickFields = ({ source = {}, keys = [] }) =>
  keys.reduce((acc, key) => {
    acc[key] = source[key] ?? defaultFormValues[key];
    return acc;
  }, {});

const cloneValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key] = cloneValue(nested);
      return acc;
    }, {});
  }
  return value;
};

export const buildFormValuesForOrganizationType = ({
  organizationType,
  currentValues = {},
}) => {
  const sharedValues = pickFields({
    source: currentValues,
    keys: SHARED_FIELD_KEYS,
  });

  if (organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
    return {
      ...defaultFormValues,
      ...sharedValues,
      ...cloneValue(agencyDefaultValues),
      organizationType: PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
    };
  }

  return {
    ...defaultFormValues,
    ...sharedValues,
    ...cloneValue(brandDefaultValues),
    organizationType: PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND,
  };
};

const pickOnboardingSession = (raw) => {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    ...createEmptyOnboardingSession(),
    startTime: typeof source.startTime === "string" ? source.startTime : "",
    endTime: typeof source.endTime === "string" ? source.endTime : "",
    uid: typeof source.uid === "string" ? source.uid : "",
    title: typeof source.title === "string" ? source.title : "",
  };
};

const pickAgencySubmitValues = (values = {}) => {
  const picked = pickFields({ source: values, keys: AGENCY_ONLY_FIELD_KEYS });
  const includePaidMediaFields = hasPaidMediaModule(picked.agencyModules);

  return {
    ...picked,
    agencyAdPlatforms: includePaidMediaFields
      ? picked.agencyAdPlatforms
      : [],
    teamFamiliarity: includePaidMediaFields ? picked.teamFamiliarity : "",
    backupContact: {
      ...createEmptyPersonContact(),
      ...(picked.backupContact || {}),
    },
    leadershipContact: {
      fullName:
        typeof picked.leadershipContact?.fullName === "string"
          ? picked.leadershipContact.fullName
          : "",
      jobTitle:
        typeof picked.leadershipContact?.jobTitle === "string"
          ? picked.leadershipContact.jobTitle
          : "",
    },
    confirmations: {
      ...createEmptyConfirmations(),
      ...(picked.confirmations || {}),
    },
    onboardingSession: pickOnboardingSession(picked.onboardingSession),
  };
};

export const sanitizePreActivationSubmitValues = (values = {}) => {
  const organizationType = getOrganizationTypeFromRecord(values);
  const sharedValues = pickFields({ source: values, keys: SHARED_FIELD_KEYS });
  sharedValues.websiteUrl = normalizePreActivationWebsiteUrl(
    sharedValues.websiteUrl
  );

  if (organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
    return {
      organizationType,
      ...sharedValues,
      ...pickAgencySubmitValues(values),
    };
  }

  const brandValues = pickFields({
    source: values,
    keys: BRAND_ONLY_FIELD_KEYS,
  });

  return {
    organizationType,
    ...sharedValues,
    ...brandValues,
    adPlatformsOther: sanitizeOtherText({
      isOther:
        Array.isArray(brandValues.adPlatforms) &&
        brandValues.adPlatforms.includes(PRE_ACTIVATION_OTHER_OPTION_VALUE),
      value: brandValues.adPlatformsOther,
    }),
  };
};
